import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Building2,
  Check,
  ChevronRight,
  HelpCircle,
  Lock,
  MapPin,
  Pencil,
  User,
} from "lucide-react";
import { AddressPicker } from "../../../../../components/address-picker";
import {
  costaRicaAddressRegistry,
  panamaAddressRegistry,
} from "../../../../../lib/addressPicker/utils/addressPicker";

const c = {
  greenDk: "#1C4A2E",
  greenMd: "#2D6B45",
  greenLt: "#4A9B6F",
  gold: "#E8A020",
  ink: "#0F1F17",
  muted: "#6B7D74",
  surface: "#F4F6F5",
  surface2: "#E8EDEA",
  border: "rgba(28,74,46,0.1)",
  white: "#FFFFFF",
  goldLt: "#FFF4DC",
};

interface UsuarioPerfil {
  nombre?: string;
  apellido?: string;
  email?: string;
  pais?: string;
  /** Provincia (texto); usado por API y Panamá. */
  provincia?: string;
  canton?: string;
  distrito?: string;
  barrio?: string;
  direccion?: string;
  /** Código INEC corregimiento (ej. 80401) para rehidratar el picker de Panamá. */
  codigo_corregimiento?: string | number;
  puntos?: number;
  foto?: string;
  telefono?: string;
  cedula?: string;
  /** Nombre de la farmacia (el GET a veces lo devuelve en `farmacia` en lugar de `farmacia_nombre`). */
  farmacia?: string;
  farmacia_nombre?: string;
  farmacia_codigo?: string;
  farmacia_id?: number;
  created_at?: string;
  [k: string]: unknown;
}

interface HomeStats {
  puntos: number;
  premios_recibidos: number;
  retos_completado: number;
  name?: string;
}

function formatPts(n: number) {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return v.toLocaleString("es-CR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function initials(nombre: string, apellido: string) {
  const a = (nombre || "").trim().charAt(0);
  const b = (apellido || "").trim().charAt(0);
  return (a + b).toUpperCase() || "U";
}

interface ProfileProps {
  setView?: React.Dispatch<React.SetStateAction<string>>;
}

interface PerfilForm {
  nombre: string;
  apellido: string;
  pais: string;
  canton: string;
  distrito: string;
  barrio: string;
  direccion: string;
  addrProvince: string | number;
  addrCanton: string | number;
  addrDistrict: string | number;
}

/** CR/PA usan el mismo esquema cantón·distrito en el JSON del picker (código distrito ≥ 5 dígitos). */
function parseDistrictCompositeCode(
  raw: string | number | undefined | null
): { addrProvince: number; addrCanton: number; addrDistrict: number } | null {
  if (raw == null || raw === "") return null;
  const n =
    typeof raw === "string" ? parseInt(String(raw).trim(), 10) : Number(raw);
  if (!Number.isFinite(n) || n < 10_000) return null;
  const dn = n % 100;
  const fullCant = Math.floor(n / 100);
  const cn = fullCant % 100;
  const pn = Math.floor(fullCant / 100);
  if (pn <= 0 || cn < 0 || dn < 0) return null;
  return { addrProvince: pn, addrCanton: cn, addrDistrict: dn };
}

function readFarmaciaId(u: UsuarioPerfil): number | undefined {
  const ext = u as Record<string, unknown>;
  const nestedFarmacia = ext.farmacia;
  const nestedId =
    nestedFarmacia &&
    typeof nestedFarmacia === "object" &&
    !Array.isArray(nestedFarmacia)
      ? (nestedFarmacia as Record<string, unknown>).farmacia_id
      : undefined;
  const raw =
    u.farmacia_id ??
    ext.farmacia_id ??
    ext.Farmacia_id ??
    ext.farmaciaId ??
    nestedId;
  if (raw == null || raw === "") return undefined;
  const n = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Texto para cruzar con el catálogo de farmacias cuando el API no manda `farmacia_id`. */
function readFarmaciaNombreParaMatch(u: UsuarioPerfil): string {
  const ext = u as Record<string, unknown>;
  const fromFarmacia = ext.farmacia;
  if (typeof fromFarmacia === "string" && fromFarmacia.trim() !== "") {
    return fromFarmacia.trim();
  }
  const n = (u.farmacia_nombre || "").trim();
  if (n !== "") return n;
  return "";
}

async function fetchFarmaciaIdByNombre(
  nombre: string
): Promise<number | undefined> {
  const token = sessionStorage.getItem("token");
  if (!token || !nombre.trim()) return undefined;
  try {
    const res = await axios.get<{ farmacia_id: number; nombre?: string }[]>(
      `${process.env.REACT_APP_API_URL}/api/farmacias/allFarmacias`,
      { headers: { "auth-token": token } }
    );
    const list = Array.isArray(res.data) ? res.data : [];
    const needle = nombre.trim().toLowerCase();
    const found = list.find(
      (f) => String(f.nombre ?? "").trim().toLowerCase() === needle
    );
    return found && Number.isFinite(found.farmacia_id)
      ? found.farmacia_id
      : undefined;
  } catch {
    return undefined;
  }
}

const Profile = ({ setView }: ProfileProps) => {
  const [user, setUser] = useState<UsuarioPerfil>({});
  const [form, setForm] = useState<PerfilForm>({
    nombre: "",
    apellido: "",
    pais: "Panamá",
    canton: "",
    distrito: "",
    barrio: "",
    direccion: "",
    addrProvince: 0,
    addrCanton: 0,
    addrDistrict: 0,
  });
  const [foto, setFoto] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [home, setHome] = useState<HomeStats | null>(null);
  const [saving, setSaving] = useState(false);
  /** Cuando el GET usuario solo trae el nombre en `farmacia`, resolvemos el id vía catálogo. */
  const [resolvedFarmaciaId, setResolvedFarmaciaId] = useState<
    number | undefined
  >(undefined);

  const getUser = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/usuarios/${sessionStorage.getItem("user_id")}`,
        {
          headers: { "auth-token": `${sessionStorage.getItem("token")}` },
        }
      );
      const u = response.data as UsuarioPerfil;
      setUser(u);
      const paisStr = (u.pais || "").toLowerCase();
      const isPa = paisStr.includes("panam");
      const isCr = paisStr.includes("costa");
      const ux = u as Record<string, unknown>;
      const codeRaw =
        u.codigo_corregimiento ??
        ux.corregimiento_codigo ??
        ux.codigo_ub_pa ??
        ux.codigo_corregimiento_pa ??
        ux.codigo_distrito;
      const addrFromCode =
        isPa || isCr
          ? parseDistrictCompositeCode(
              codeRaw as string | number | undefined | null
            )
          : null;

      let addrProvince = addrFromCode?.addrProvince ?? 0;
      let addrCanton = addrFromCode?.addrCanton ?? 0;
      let addrDistrict = addrFromCode?.addrDistrict ?? 0;

      if (
        (isPa || isCr) &&
        (addrProvince === 0 || addrCanton === 0 || addrDistrict === 0)
      ) {
        const registry = isPa ? panamaAddressRegistry : costaRicaAddressRegistry;
        const fromLabels = registry.matchLabelsToCodes(
          String(u.provincia ?? "").trim(),
          String(u.canton ?? "").trim(),
          String(u.distrito ?? "").trim()
        );
        if (fromLabels) {
          addrProvince = fromLabels.province;
          addrCanton = fromLabels.canton;
          addrDistrict = fromLabels.district;
        }
      }

      setForm({
        nombre: u.nombre || "",
        apellido: u.apellido || "",
        pais: u.pais || "Panamá",
        canton: u.canton || "",
        distrito: u.distrito || "",
        barrio: u.barrio || "",
        direccion: u.direccion || "",
        addrProvince,
        addrCanton,
        addrDistrict,
      });
      if (u.foto) {
        try {
          const { data: s3Data } = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/s3/signed-url?key=${encodeURIComponent(u.foto)}`,
            { headers: { "auth-token": sessionStorage.getItem("token") } }
          );
          setFoto(s3Data.url);
        } catch {
          setFoto(null);
        }
      } else {
        setFoto(null);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const getHome = useCallback(async () => {
    const email = sessionStorage.getItem("email");
    if (!email) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/usuarios/home/${email}`,
        {
          headers: { "auth-token": `${sessionStorage.getItem("token")}` },
          params: { email },
        }
      );
      setHome(response.data as HomeStats);
    } catch {
      setHome(null);
    }
  }, []);

  useEffect(() => {
    getUser();
    getHome();
  }, [getUser, getHome]);

  useEffect(() => {
    let cancelled = false;

    const directId = readFarmaciaId(user);
    if (directId !== undefined) {
      setResolvedFarmaciaId(directId);
      return () => {
        cancelled = true;
      };
    }

    const nombreFarmacia = readFarmaciaNombreParaMatch(user);
    if (nombreFarmacia === "") {
      setResolvedFarmaciaId(undefined);
      return () => {
        cancelled = true;
      };
    }

    void fetchFarmaciaIdByNombre(nombreFarmacia).then((id) => {
      if (!cancelled) setResolvedFarmaciaId(id);
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const displayName = `${form.nombre} ${form.apellido}`.trim() || "Usuario";
  const displayEmail = user.email || sessionStorage.getItem("email") || "";
  const pts = home?.puntos ?? user.puntos ?? 0;
  const paisIsPanama = (form.pais || "").toLowerCase().includes("panam");
  const paisIsCostaRica = (form.pais || "").toLowerCase().includes("costa");

  const onAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const r = new FileReader();
    r.onload = () => setAvatarPreview(r.result as string);
    r.readAsDataURL(file);
    e.target.value = "";
  };

  const updateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const paisSubmitIsPa = (form.pais || "").toLowerCase().includes("panam");
      const paisSubmitIsCr = (form.pais || "").toLowerCase().includes("costa");

      const data: Record<string, string | number | undefined> = {
        nombre: form.nombre,
        apellido: form.apellido,
        pais: form.pais,
        canton: form.canton,
        distrito: form.distrito,
        direccion: form.direccion,
      };

      let farmaciaId = readFarmaciaId(user) ?? resolvedFarmaciaId;
      if (farmaciaId === undefined) {
        farmaciaId = await fetchFarmaciaIdByNombre(
          readFarmaciaNombreParaMatch(user)
        );
      }
      if (farmaciaId !== undefined) {
        data.farmacia_id = farmaciaId;
      }

      if (paisSubmitIsPa) {
        data.barrio = "";
      } else {
        data.barrio = form.barrio;
      }

      if (paisSubmitIsPa) {
        const labels = panamaAddressRegistry.resolveLocationLabelsForDb(
          form.addrProvince,
          form.addrCanton,
          form.addrDistrict
        );
        if (labels.province) {
          data.provincia = labels.province;
        }
      } else if (paisSubmitIsCr) {
        const labels = costaRicaAddressRegistry.resolveLocationLabelsForDb(
          form.addrProvince,
          form.addrCanton,
          form.addrDistrict
        );
        if (labels.province) {
          data.provincia = labels.province;
        }
      }

      if (selectedFile) {
        data.foto = `usuarios/${form.nombre}-${form.apellido}-${selectedFile.name}`;
      }
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/usuarios/${sessionStorage.getItem("user_id")}`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": `${sessionStorage.getItem("token")}`,
          },
        }
      );
      if (selectedFile) {
        const fileKey = `usuarios/${form.nombre}-${form.apellido}-${selectedFile.name}`;
        const { data: uploadData } = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/s3/upload-url`,
          { key: fileKey },
          { headers: { "auth-token": sessionStorage.getItem("token") } }
        );
        await axios.put(uploadData.url, selectedFile, { headers: { "Content-Type": selectedFile.type } });
        setSelectedFile(null);
        setAvatarPreview(null);
      }
      await getUser();
      await getHome();
      Swal.fire({
        icon: "success",
        title: "Perfil actualizado",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Error al actualizar el perfil",
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setSaving(false);
    }
  };

  const created =
    user.created_at &&
    !Number.isNaN(Date.parse(String(user.created_at)))
      ? new Date(String(user.created_at)).toLocaleDateString("es-CR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
      <form
        onSubmit={updateProfile}
        className="mx-auto flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
        style={{ background: c.surface }}
      >
        <div
          className="md:mx-auto flex min-h-0 w-full min-w-0 max-w-7xl flex-1 flex-col overflow-y-auto rounded-none pb-3 shadow-none md:rounded-2xl md:border md:border-gray-200/80 md:shadow-sm"
          style={{ scrollbarGutter: "stable" }}
        >
          {/* Cover + profile row (social-style on desktop) */}
          <div
            className="relative overflow-hidden md:rounded-t-2xl"
            style={{
              background: `linear-gradient(160deg, ${c.greenDk} 0%, ${c.greenMd} 55%, ${c.greenLt} 100%)`,
            }}
          >
            <div
              className="h-16 sm:h-20 md:h-28 md:min-h-[7.5rem] lg:h-32"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -right-8 top-0 h-36 w-36 rounded-full md:right-10 md:top-4"
              style={{ background: "rgba(255,255,255,0.06)" }}
            />
            <div
              className="pointer-events-none absolute -left-6 bottom-0 h-24 w-24 rounded-full opacity-90 md:bottom-8"
              style={{ background: "rgba(255,255,255,0.04)" }}
            />

            <div className="relative z-[1] mx-auto w-full max-w-6xl px-4 pb-5 pt-0 sm:px-5 md:px-8 md:pb-6">
              <div className="flex flex-col items-center text-center sm:items-center md:flex-row md:items-end md:gap-6 md:text-left">
                <div className="relative -mt-11 shrink-0 sm:-mt-14 md:-mt-16">
                  <div
                    className="flex h-[88px] w-[88px] items-center justify-center overflow-hidden rounded-full border-[3px] text-3xl font-extrabold text-white sm:h-24 sm:w-24 sm:text-3xl md:h-[108px] md:w-[108px] lg:h-[120px] lg:w-[120px] lg:text-4xl"
                    style={{
                      borderColor: "rgba(255,255,255,0.55)",
                      background: "rgba(255,255,255,0.2)",
                      boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                    }}
                  >
                    {avatarPreview || foto ? (
                      <img
                        src={avatarPreview || foto || ""}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      initials(form.nombre, form.apellido)
                    )}
                  </div>
                  <label
                    className="absolute bottom-0.5 right-0.5 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-white transition hover:scale-110 md:bottom-1 md:right-1"
                    style={{ background: c.gold }}
                  >
                    <Pencil className="h-3.5 w-3.5 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={onAvatarChange}
                    />
                  </label>
                </div>

                <div className="mt-3 min-w-0 flex-1 md:mt-0 md:pb-0.5">
                  <h1 className="text-balance text-xl font-extrabold text-white sm:text-2xl md:text-3xl">
                    {displayName}
                  </h1>
                  <p className="mt-1 break-all text-sm text-white/70 md:text-base">
                    {displayEmail}
                  </p>
                  <div
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/25 px-2.5 py-1.5 text-[0.7rem] font-bold text-white backdrop-blur-sm sm:text-xs"
                    style={{ background: "rgba(255,255,255,0.12)" }}
                  >
                    {formatPts(pts)} pts
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="relative z-10 -mt-4 mx-auto w-full max-w-6xl overflow-hidden rounded-2xl border border-[1.5px] bg-white px-0 py-3.5 shadow-lg sm:-mt-5 sm:px-4 md:px-8"
            style={{ borderColor: c.border, boxShadow: "0 4px 24px rgba(0,0,0,0.08)" }}
          >
            <div className="text-center">
              <div
                className="text-lg font-extrabold leading-none sm:text-[1.3rem]"
                style={{ color: c.gold }}
              >
                {formatPts(pts)}
              </div>
              <div
                className="mt-1 text-[0.6rem] leading-tight sm:text-[0.68rem]"
                style={{ color: c.muted }}
              >
                Celmar monedas
              </div>
            </div>
          </div>

          <div className="mt-4 grid w-full max-w-6xl grid-cols-1 gap-4 px-3 pb-3 sm:px-4 md:mt-5 md:px-8 md:pb-4 lg:grid-cols-2 lg:items-start lg:gap-5">
            <div
              className="overflow-hidden rounded-2xl border border-[1.5px] bg-white"
              style={{ borderColor: c.border }}
            >
              <div
                className="flex items-center gap-2 border-b px-3 py-2.5"
                style={{ borderColor: c.border }}
              >
                <User className="h-[18px] w-[18px] shrink-0" style={{ color: c.greenMd }} />
                <span className="text-sm font-bold" style={{ color: c.ink }}>
                  Información personal
                </span>
              </div>
              <Field
                label="Nombre"
                value={form.nombre}
                onChange={(v) => setForm((f) => ({ ...f, nombre: v }))}
              />
              <Field
                label="Apellido"
                value={form.apellido}
                onChange={(v) => setForm((f) => ({ ...f, apellido: v }))}
              />
              <Field
                label="Correo electrónico"
                value={displayEmail}
                onChange={() => {}}
                readOnly
              />
              <Field
                label="Teléfono"
                value={String(user.telefono ?? "")}
                onChange={() => {}}
                readOnly
                placeholder="—"
                optionalEmpty
              />
              <Field
                label="Cédula / RUC"
                value={String(user.cedula ?? "")}
                onChange={() => {}}
                readOnly
                placeholder="—"
                optionalEmpty
              />
            </div>

            <div
              className="overflow-hidden rounded-2xl border border-[1.5px] bg-white"
              style={{ borderColor: c.border }}
            >
              <div
                className="flex items-center gap-2 border-b px-3 py-2.5"
                style={{ borderColor: c.border }}
              >
                <Building2 className="h-[18px] w-[18px] shrink-0" style={{ color: c.greenMd }} />
                <span className="text-sm font-bold" style={{ color: c.ink }}>
                  Farmacia
                </span>
              </div>
              <Field
                label="Nombre de la farmacia"
                value={String(user.farmacia_nombre ?? "")}
                onChange={() => {}}
                readOnly
                placeholder="—"
                optionalEmpty
              />
              <Field
                label="Código de farmacia"
                value={String(user.farmacia_codigo ?? "")}
                onChange={() => {}}
                readOnly
                placeholder="—"
                optionalEmpty
              />
              <Field
                label="Fecha de registro"
                value={created ?? ""}
                onChange={() => {}}
                readOnly
                optionalEmpty
                placeholder="—"
              />
            </div>

            <div
              className="overflow-hidden rounded-2xl border border-[1.5px] bg-white"
              style={{ borderColor: c.border }}
            >
              <div
                className="flex items-center gap-2 border-b px-3 py-2.5"
                style={{ borderColor: c.border }}
              >
                <MapPin className="h-[18px] w-[18px] shrink-0" style={{ color: c.greenMd }} />
                <span className="text-sm font-bold" style={{ color: c.ink }}>
                  Dirección de entrega
                </span>
              </div>
              <div className="border-b border-[rgba(28,74,46,0.1)] px-3 py-2.5">
                <label
                  className="text-[0.58rem] font-semibold uppercase tracking-wider"
                  style={{ color: c.muted }}
                >
                  País
                </label>
                <select
                  className="mt-1 w-full rounded-[10px] border border-[1.5px] bg-[#F4F6F5] px-2.5 py-2 text-sm outline-none focus:border-[#2D6B45] focus:bg-white focus:ring-[3px] focus:ring-[#2D6B45]/10"
                  style={{ color: c.ink, borderColor: c.border }}
                  value={form.pais}
                  onChange={(e) => {
                    const pais = e.target.value;
                    setForm((f) => ({
                      ...f,
                      pais,
                      addrProvince: 0,
                      addrCanton: 0,
                      addrDistrict: 0,
                    }));
                  }}
                >
                  <option value="Panamá">Panamá</option>
                  <option value="Costa Rica">Costa Rica</option>
                  <option value="Nicaragua">Nicaragua</option>
                </select>
              </div>
              {paisIsPanama ? (
                <div className="border-b border-[rgba(28,74,46,0.1)] px-3 py-2.5">
                  <p
                    className="mb-2 text-[0.58rem] font-semibold uppercase tracking-wider"
                    style={{ color: c.muted }}
                  >
                    Ubicación
                  </p>
                  <AddressPicker
                    source="pa"
                    address={form.direccion}
                    province={form.addrProvince}
                    canton={form.addrCanton}
                    district={form.addrDistrict}
                    useFixedPosition
                    onChange={(v) => {
                      const labels =
                        panamaAddressRegistry.resolveLocationLabelsForDb(
                          v.province,
                          v.canton,
                          v.district
                        );
                      setForm((f) => ({
                        ...f,
                        direccion: v.address,
                        addrProvince: v.province,
                        addrCanton: v.canton,
                        addrDistrict: v.district,
                        canton:
                          labels.canton != null &&
                          String(labels.canton).trim() !== ""
                            ? String(labels.canton).trim()
                            : f.canton,
                        distrito:
                          labels.district != null && labels.district !== ""
                            ? labels.district
                            : f.distrito,
                      }));
                    }}
                  />
                </div>
              ) : paisIsCostaRica ? (
                <div className="border-b border-[rgba(28,74,46,0.1)] px-3 py-2.5">
                  <p
                    className="mb-2 text-[0.58rem] font-semibold uppercase tracking-wider"
                    style={{ color: c.muted }}
                  >
                    Ubicación
                  </p>
                  <AddressPicker
                    source="cr"
                    address={form.direccion}
                    province={form.addrProvince}
                    canton={form.addrCanton}
                    district={form.addrDistrict}
                    useFixedPosition
                    onChange={(v) => {
                      const labels =
                        costaRicaAddressRegistry.resolveLocationLabelsForDb(
                          v.province,
                          v.canton,
                          v.district
                        );
                      setForm((f) => ({
                        ...f,
                        direccion: v.address,
                        addrProvince: v.province,
                        addrCanton: v.canton,
                        addrDistrict: v.district,
                        canton:
                          labels.canton != null &&
                          String(labels.canton).trim() !== ""
                            ? String(labels.canton).trim()
                            : f.canton,
                        distrito:
                          labels.district != null && labels.district !== ""
                            ? String(labels.district).trim()
                            : f.distrito,
                      }));
                    }}
                  />
                </div>
              ) : (
                <>
                  <Field
                    label="Provincia / cantón"
                    value={form.canton}
                    onChange={(v) => setForm((f) => ({ ...f, canton: v }))}
                    placeholder="Ej. San José"
                  />
                  <Field
                    label="Distrito"
                    value={form.distrito}
                    onChange={(v) => setForm((f) => ({ ...f, distrito: v }))}
                    placeholder="Distrito"
                  />
                </>
              )}
              {paisIsCostaRica ? (
                <Field
                  label="Barrio"
                  value={form.barrio}
                  onChange={(v) => setForm((f) => ({ ...f, barrio: v }))}
                  placeholder="Barrio"
                />
              ) : null}
              {!paisIsPanama && !paisIsCostaRica ? (
                <Field
                  label="Dirección exacta"
                  value={form.direccion}
                  onChange={(v) => setForm((f) => ({ ...f, direccion: v }))}
                  placeholder="Ej. 100m norte del Banco Nacional"
                />
              ) : null}
            </div>

            <div
              className="overflow-hidden rounded-2xl border border-[1.5px] bg-white"
              style={{ borderColor: c.border }}
            >
              <div
                className="flex items-center gap-2 border-b px-3 py-2.5"
                style={{ borderColor: c.border }}
              >
                <Lock
                  className="h-[18px] w-[18px] shrink-0"
                  style={{ color: c.greenMd }}
                />
                <span className="text-sm font-bold" style={{ color: c.ink }}>
                  Cuenta
                </span>
              </div>
              <button
                type="button"
                className="flex w-full items-center gap-2.5 border-b px-3 py-3 text-left transition last:border-0 hover:bg-gray-50"
                style={{ borderColor: c.border }}
                onClick={() => {
                  window.open("/password-reset", "_blank", "noopener,noreferrer");
                }}
              >
                <span
                  className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px]"
                  style={{ background: c.goldLt }}
                >
                  <Lock className="h-4 w-4" style={{ color: c.gold }} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold" style={{ color: c.ink }}>
                    Cambiar contraseña
                  </p>
                  <p className="text-[0.7rem] text-gray-500">
                    Te enviamos al flujo de recuperación
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
              </button>
              {setView ? (
                <button
                  type="button"
                  className="flex w-full items-center gap-2.5 px-3 py-3 text-left transition hover:bg-gray-50"
                  onClick={() => setView("Ayuda")}
                >
                  <span
                    className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] bg-slate-100"
                  >
                    <HelpCircle className="h-4 w-4 text-slate-600" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold" style={{ color: c.ink }}>
                      Centro de ayuda
                    </p>
                    <p className="text-[0.7rem] text-gray-500">
                      Contacto y soporte
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
                </button>
              ) : null}
            </div>

            <div className="pt-0.5 pb-2 lg:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-base font-extrabold text-white shadow-lg transition disabled:opacity-60"
                style={{
                  background: c.greenMd,
                  boxShadow: "0 4px 18px rgba(45,107,69,0.35)",
                }}
              >
                {saving ? (
                  "Guardando…"
                ) : (
                  <>
                    <Check className="h-[18px] w-[18px]" />
                    Guardar cambios
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>

    </div>
  );
};

function Field({
  label,
  value,
  onChange,
  readOnly,
  placeholder,
  optionalEmpty,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
  placeholder?: string;
  optionalEmpty?: boolean;
  className?: string;
}) {
  const showEmpty = optionalEmpty && !value;
  return (
    <div
      className={`border-b border-[rgba(28,74,46,0.1)] px-3 py-2.5 last:border-0 ${className}`}
    >
      <label
        className="text-[0.58rem] font-semibold uppercase tracking-wider"
        style={{ color: c.muted }}
      >
        {label}
      </label>
      {readOnly ? (
        <p
          className="mt-1.5 text-sm font-semibold"
          style={{ color: showEmpty ? c.muted : c.ink }}
        >
          {showEmpty ? (placeholder ?? "—") : value}
        </p>
      ) : (
        <input
          className="mt-1.5 w-full rounded-[10px] border border-[1.5px] bg-[#F4F6F5] px-2.5 py-2 text-sm text-[#0F1F17] outline-none focus:border-[#2D6B45] focus:bg-white focus:ring-[3px] focus:ring-[#2D6B45]/10"
          style={{ borderColor: c.border }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

export default Profile;
